// עזרים משותפים למסכי הניהול.
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiError, type Job } from '../../api';
import { JobBadge, useToast } from '../ui';
import './mgmt.css';

/** פעולה אסינכרונית עם מצב busy, שגיאה מהשרת והודעת הצלחה. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>, okMsg?: string): Promise<T | undefined> => {
    setBusy(true); setError(null);
    try {
      const r = await fn();
      if (okMsg) toast(okMsg);
      return r;
    } catch (e) {
      const msg = errMsg(e);
      setError(msg);
      toast(msg, 'err');
      return undefined;
    } finally { setBusy(false); }
  }, [toast]);
  return { busy, error, setError, run };
}

export function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.details as unknown;
    if (Array.isArray(d) && d.length && d.every((x) => typeof x === 'string')) return `${e.message} (${d.join(', ')})`;
    if (Array.isArray(d) && d.length && typeof d[0] === 'object' && d[0] && 'message' in (d[0] as object)) {
      return `${e.message}: ${(d as { path?: unknown[]; message: string }[]).map((x) => `${(x.path ?? []).join('.')} ${x.message}`).join('; ')}`;
    }
    if (e.status === 404 && /^שגיאה 404/.test(e.message)) return 'הנתיב אינו זמין בשרת עדיין (404)';
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** כותרת עמוד אחידה */
export function PageHead({ title, sub, crumbs, children }: { title: ReactNode; sub?: ReactNode; crumbs?: ReactNode; children?: ReactNode }) {
  useEffect(() => { if (typeof title === 'string') document.title = `${title} · בונה באנרים`; }, [title]);
  return (
    <div className="page-head page-title">
      <div className="grow">
        {crumbs && <nav className="crumbs" aria-label="פירורי לחם">{crumbs}</nav>}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {children && <div className="row">{children}</div>}
    </div>
  );
}

/** לשוניות נגישות */
export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (t: T) => void; label: string }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: React.KeyboardEvent, i: number) => {
    // RTL: חץ שמאלה = הבא
    let n = -1;
    if (e.key === 'ArrowLeft') n = (i + 1) % tabs.length;
    if (e.key === 'ArrowRight') n = (i - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') n = 0;
    if (e.key === 'End') n = tabs.length - 1;
    if (n >= 0) { e.preventDefault(); onChange(tabs[n].id); refs.current[n]?.focus(); }
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button key={t.id} ref={(el) => { refs.current[i] = el; }} role="tab" type="button" aria-selected={value === t.id} tabIndex={value === t.id ? 0 : -1}
          className={value === t.id ? 'active' : ''} onClick={() => onChange(t.id)} onKeyDown={(e) => onKey(e, i)}>{t.label}</button>
      ))}
    </div>
  );
}

export function CopyButton({ text, label = 'העתקה' }: { text: string | null | undefined; label?: string }) {
  const toast = useToast();
  const copy = async () => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast('הועתק'); }
    catch {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast('הועתק'); } catch { toast('ההעתקה נכשלה', 'err'); }
      ta.remove();
    }
  };
  return <button type="button" className="btn small" onClick={copy} disabled={!text} aria-label={`${label}${text ? `: ${text}` : ''}`}>{label}</button>;
}

/** אישור לפני פעולה הרסנית/בלתי הפיכה */
export function confirmAction(msg: string) { return window.confirm(msg); }

export const SPEC_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'טיוטה', cls: '' },
  pending_approval: { label: 'ממתין לאישור', cls: 'warn' },
  approved: { label: 'מאושר', cls: 'ok' },
  superseded: { label: 'הוחלף', cls: 'info' },
};
export function SpecStatusBadge({ status, isDemo }: { status: string | null | undefined; isDemo?: boolean }) {
  if (!status) return <span className="badge err">אין מפרט</span>;
  const s = SPEC_STATUS[status] ?? { label: status, cls: '' };
  return <span className="row" style={{ gap: 4, display: 'inline-flex' }}><span className={`badge ${s.cls}`}>{s.label}</span>{isDemo && <span className="badge warn">הדגמה בלבד</span>}</span>;
}

export const CAMPAIGN_STATUS: Record<string, string> = { draft: 'טיוטה', active: 'פעיל', done: 'הסתיים', archived: 'ארכיון' };
export const LANGS: Record<string, string> = { he: 'עברית', en: 'אנגלית', ar: 'ערבית', ru: 'רוסית' };
export const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,79}$/;
export const KEY_HINT = 'אותיות לטיניות קטנות, ספרות, - ו-_ (מתחיל באות או ספרה)';
export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** תצוגת התקדמות עבודה (aria-live) */
export function JobProgress({ job, busy, label }: { job: Job | null; busy?: boolean; label?: string }) {
  if (!job && !busy) return null;
  const p = job?.progress as { pct?: number; percent?: number; step?: string; message?: string; done?: number; total?: number } | null | undefined;
  const pct = p?.pct ?? p?.percent ?? (p?.total ? Math.round(((p.done ?? 0) / p.total) * 100) : undefined);
  return (
    <div className="stack" role="status" aria-live="polite" style={{ gap: 4 }}>
      <div className="row">
        {label && <strong>{label}</strong>}
        {job ? <JobBadge status={job.status} /> : <span className="badge info">שולח…</span>}
        {(p?.step || p?.message) && <span className="small muted">{p.step ?? p.message}</span>}
        {job && <span className="small muted mono">{job.id}</span>}
      </div>
      {typeof pct === 'number' && job?.status === 'running' && <div className="progress" aria-hidden="true"><span style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} /></div>}
      {job?.status === 'failed' && job.error && <div className="alert err" role="alert">{job.error}</div>}
    </div>
  );
}

/** הצגת ערך כללי (מערך/אובייקט/בוליאני) בקריאות */
export function fmtValue(v: unknown): string {
  if (v === 'unknown' || v === undefined || v === null) return 'לא ידוע';
  if (typeof v === 'boolean') return v ? 'כן' : 'לא';
  if (Array.isArray(v)) {
    if (v.every((x) => x && typeof x === 'object' && 'width' in x && 'height' in x)) return v.map((x: { width: number; height: number }) => `${x.width}×${x.height}`).join(', ');
    return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** קלט תגיות מופרדות בפסיק */
export function TagsInput({ value, onChange, label = 'תגיות' }: { value: string[]; onChange: (v: string[]) => void; label?: string }) {
  const [text, setText] = useState(value.join(', '));
  useEffect(() => { setText(value.join(', ')); }, [value.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const id = useId();
  return (
    <label className="field" htmlFor={id}><span>{label}</span>
      <input id={id} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onChange(text.split(',').map((t) => t.trim()).filter(Boolean))} placeholder="מופרד בפסיקים" />
    </label>
  );
}

export function Check({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; disabled?: boolean }) {
  return <label className="checkline"><input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />{children}</label>;
}

/** שורת diff של שדה */
export function DiffList({ changes, labels }: { changes: { field?: string; key?: string; before: unknown; after: unknown }[]; labels?: Record<string, string> }) {
  if (!changes.length) return <div className="muted small">אין הבדלים.</div>;
  return (
    <div>
      {changes.map((c, i) => {
        const k = c.field ?? c.key ?? '';
        return (
          <div className="diff-row" key={i}>
            <strong>{labels?.[k] ?? k}</strong>
            <div className="stack" style={{ gap: 4 }}>
              <div><span className="small muted">לפני: </span><DiffVal v={c.before} cls="diff-before" /></div>
              <div><span className="small muted">אחרי: </span><DiffVal v={c.after} cls="diff-after" /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function DiffVal({ v, cls }: { v: unknown; cls: string }) {
  if (v === 'unknown' || v === undefined || v === null || v === '') return <span className={`${cls} unknown`}>{v === '' ? '(ריק)' : 'לא ידוע'}</span>;
  if (typeof v === 'object') return <pre className={`json-val ${cls}`}>{JSON.stringify(v, null, 1)}</pre>;
  return <span className={`${cls} ${typeof v === 'string' && /^[\x00-\x7F]*$/.test(v) ? 'ltr' : ''}`}>{fmtValue(v)}</span>;
}

export const KIT_FIELD_LABELS: Record<string, string> = {
  logos: 'לוגואים', colors: 'צבעים', fonts: 'פונטים', logoClearSpacePx: 'מרווח ריק סביב הלוגו (px)', logoMinWidthPx: 'רוחב לוגו מינימלי (px)',
  minFontSizePx: 'גודל פונט מינימלי (px)', imageStyle: 'סגנון תמונות', animationStyle: 'סגנון אנימציה', contentGuidelines: 'הנחיות תוכן',
  guideText: 'טקסט מדריך המותג', approvedExampleAssetIds: 'דוגמאות מאושרות',
};

/** fetch בטוח שמחזיר null ב-404 (לנתיבים שנבנים במקביל) */
export function isMissingRoute(e: unknown) { return e instanceof ApiError && e.status === 404; }
